import React, { useEffect, useState } from "react";

import { ActionIcon, Box, Flex, Menu, Text } from "@mantine/core";
import { useSelector } from "react-redux";
import AppNavbar from "../../../components/AppNavbar/AppNavbar";
import {
  IconPlus,
  IconSettings,
  IconSearch,
  IconEye,
  IconEdit,
  IconTrash,
  IconArrowRight,
  IconFiles,
  IconUnlink,
} from "@tabler/icons-react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import NoProviderSvg from "../../../components/Svgs/NoProviderSvg";
import { BiDotsVerticalRounded } from "react-icons/bi";
import useStyles from "./style";
import CustomBillingPagination from "../../../components/Pagination/CustomBillingPagination";
import {
  deleteSkypointProvider,
  enterProviderView,
  getAllSkypointProvider,
  getProviderSettings,
  unlinkSkypointProvider,
} from "../../../services/skypointProvider";
import TableLoader from "../../../components/TableLoader";
import swal from "sweetalert";
import moment from "moment";
import useDebounce from "../../../utils/useDebounce";
import { acceptProviderRequest } from "../../../services/notification";
import AddProviderDrawer from "../../../components/AddProviderDrawer";
import { canAddProviders } from "../../../utils/providerPermissions";

const TYPE_TABS = [
  { value: "", label: "All" },
  { value: "ogiso", label: "Ogiso" },
  { value: "external", label: "External" },
];

const SkypointProvider = () => {
  const { classes, cx } = useStyles();
  const [searchParams, setSearchParams] = useSearchParams();

  // The Deleted/Pending status tabs were removed — the provider list only
  // ever shows active providers now. Sent statically to the API below (the
  // backend still accepts the filter param, it's just never anything else
  // from this page).
  const selectedTab = "active";
  const { profile } = useSelector((state) => state?.profile);
  const {
    skypointProviders,
    skypointProvidersCount,
    skypointProvidersLoading,
    providerSettings,
  } = useSelector((state) => state?.skypointProviders);
  const navigate = useNavigate();
  const { state: locationState } = useLocation();
  const [currentPage, setCurrentPage] = useState(1);
  const [providerTypeFilter, setProviderTypeFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounce(searchTerm, 500);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const showExternalProviders = providerSettings?.showExternalProviders !== false;
  const typeTabs = showExternalProviders
    ? TYPE_TABS
    : TYPE_TABS.filter((t) => t.value !== "external");
  const visibleProviders = showExternalProviders
    ? skypointProviders
    : skypointProviders?.filter((p) => !p?.isExternal);

  // A search or type filter that matches nothing still has to keep its own
  // controls on screen. Swapping the whole card for the page-level empty state
  // took the search box away with it, so there was no way to edit or clear the
  // query that emptied the list — the search appeared to delete itself. The
  // big "No Provider Found" panel is for an account that genuinely has none.
  const isFiltered = !!debouncedSearch?.trim() || !!providerTypeFilter;
  const hasRows = visibleProviders?.length > 0;
  const showTableCard = skypointProvidersLoading || hasRows || isFiltered;

  // "Showing 7 providers · 4 external, 3 Ogiso". The breakdown counts the rows
  // actually on screen (one page's worth), so it always adds up to the number
  // in front of it. Dropped entirely while a type filter is active, where every
  // row is the same type and restating that reads as noise.
  const externalCount = visibleProviders?.filter((p) => p?.isExternal).length || 0;
  const ogisoCount = (visibleProviders?.length || 0) - externalCount;
  const pagInfo = [
    `Showing ${visibleProviders?.length || 0} provider${visibleProviders?.length === 1 ? "" : "s"}`,
    !providerTypeFilter && hasRows
      ? [externalCount ? `${externalCount} external` : "", ogisoCount ? `${ogisoCount} Ogiso` : ""]
          .filter(Boolean)
          .join(", ")
      : "",
  ]
    .filter(Boolean)
    .join(" · ");

  useEffect(() => {
    if (!providerSettings) getProviderSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle email accept link for provider requests
  useEffect(() => {
    const action = searchParams.get("provider-action");
    const notificationId = searchParams.get("notificationId");
    const providerId = searchParams.get("providerId");

    if (action === "accept" && notificationId && providerId) {
      (async () => {
        const success = await acceptProviderRequest(providerId, notificationId);
        if (success) {
          swal({ title: "Provider request accepted!", icon: "success" });
          // Refresh the provider list
          getAllSkypointProvider(1, "active");
        } else {
          swal({ title: "Could not accept request", text: "The request may have already been accepted or is no longer valid.", icon: "error" });
        }
        // Clear query params from URL
        searchParams.delete("provider-action");
        searchParams.delete("notificationId");
        searchParams.delete("providerId");
        setSearchParams(searchParams, { replace: true });
      })();
    }
  }, []);

  const handleAddProvider = () => {
    setDrawerOpen(true);
  };

  // The retired /provider-skypoint/add URL redirects here asking for the
  // drawer (see SkypointAddProvider). Cleared from history straight away so a
  // later back/forward navigation doesn't reopen it.
  useEffect(() => {
    if (locationState?.openAdd) {
      setDrawerOpen(true);
      navigate(".", { replace: true, state: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationState?.openAdd]);

  const refreshList = () => {
    getAllSkypointProvider(
      currentPage,
      selectedTab,
      debouncedSearch,
      "",
      true,
      providerTypeFilter
    );
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Each provider type opens what it actually has. An external provider has no
  // Ogiso account to enter, so the row opens their profile page — the same
  // place the "View provider" menu item goes. An Ogiso provider drops you into
  // their own dashboard.
  const handleRowClick = async (item) => {
    if (item?.skypointApprovalStatus === "pending") return;

    if (item?.isExternal) {
      navigate(`/home/provider-skypoint/view`, { state: { data: item } });
      return;
    }

    try {
      if (selectedTab === "active") {
        const success = await enterProviderView(
          item?.id,
          item?.businessName || item?.businessProfile?.companyName || item?.name || "Provider"
        );
        if (success) {
          navigate("/home/provider-view");
        }
      }
    } catch (error) {}
  };

  // An Ogiso provider owns their own Ogiso account, so the most we may do is
  // drop the link to our agency — deleting would take their live account with
  // it. External providers are records we created here, so those are deleted.
  const handleUnlinkProvider = async (item) => {
    const confirmed = await swal({
      title: "Remove Provider?",
      text: "This will remove the provider from your list. Their Ogiso account will not be deleted, and you can add them again later.",
      icon: "warning",
      dangerMode: true,
      buttons: ["Cancel", "Remove"],
    });
    if (confirmed) {
      const success = await unlinkSkypointProvider(item?.id || item?._id);
      if (success) {
        refreshList();
      }
    }
  };

  const handleDeleteProvider = async (item) => {
    const confirmed = await swal({
      title: "Delete Provider?",
      text: "This will delete the provider and their documents from your list. This action cannot be undone.",
      icon: "warning",
      dangerMode: true,
      buttons: ["Cancel", "Delete"],
    });
    if (confirmed) {
      await deleteSkypointProvider(item?.id || item?._id);
      refreshList();
    }
  };

  const handleProviderTypeChange = (value) => {
    setCurrentPage(1);
    setProviderTypeFilter(value || "");
  };

  const handleSearchChange = (e) => {
    setCurrentPage(1);
    setSearchTerm(e.target.value);
  };

  useEffect(() => {
    refreshList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, providerTypeFilter, debouncedSearch]);

  return (
    <Box className={classes.content}>
      <AppNavbar title="Providers" profile={profile} />
      <Box px={20}>
        <Box className={classes.toolbarCard}>
          <Flex gap={10} align="center">
            {canAddProviders(profile?.user, providerSettings?.whoCanAdd) && (
              <button
                type="button"
                className={classes.btnAdd}
                onClick={handleAddProvider}
              >
                <IconPlus size={15} stroke={2.4} />
                Add Provider
              </button>
            )}
          </Flex>
          <Flex gap={10} align="center">
            <ActionIcon
              className={classes.iconBtnSquare}
              aria-label="Provider settings"
              onClick={() => navigate("/home/provider-skypoint-settings")}
            >
              <IconSettings size={17} stroke={1.8} />
            </ActionIcon>
          </Flex>
        </Box>

        {showTableCard && (
          <Box className={classes.tableWrap}>
            <Box className={classes.cardToolbar}>
              <Box className={classes.segmented}>
                {typeTabs.map((t) => (
                  <button
                    key={t.value || "all"}
                    type="button"
                    className={cx(classes.segItem, {
                      [classes.segItemOn]: providerTypeFilter === t.value,
                    })}
                    onClick={() => handleProviderTypeChange(t.value)}
                  >
                    {t.label}
                  </button>
                ))}
              </Box>
              <Box className={classes.search}>
                <IconSearch size={15} stroke={2} />
                <input
                  placeholder="Search provider, EHR or contact"
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
              </Box>
            </Box>
            <Box style={{ overflowX: "auto" }}>
              <table className={classes.tableRoot}>
                <thead>
                  <tr>
                    <th>Provider</th>
                    <th>Type</th>
                    <th>EHR</th>
                    <th>Documents</th>
                    <th>Added</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {skypointProvidersLoading ? (
                    <tr>
                      <td colSpan={1000}>
                        <TableLoader />
                      </td>
                    </tr>
                  ) : !hasRows ? (
                    <tr>
                      <td colSpan={6} className={classes.emptyCell}>
                        {isFiltered
                          ? "No providers match your search."
                          : "No providers to show."}
                      </td>
                    </tr>
                  ) : (
                    visibleProviders?.map((item) => {
                      const isPending = item?.skypointApprovalStatus === "pending";
                      const isExternal = !!item?.isExternal;
                      // An external provider's profile is viewable from any
                      // tab; entering an Ogiso provider's dashboard only makes
                      // sense while the link is active.
                      const isClickable =
                        !isPending && (isExternal || selectedTab === "active");
                      const canEnterProviderView =
                        !isPending && !isExternal && selectedTab === "active";
                      const providerName =
                        item.businessName || item.businessProfile?.companyName || "-";

                      return (
                        <tr
                          key={item?.id}
                          className={classes.row}
                          style={!isClickable ? { cursor: "default" } : {}}
                          onClick={() => isClickable && handleRowClick(item)}
                        >
                          <td className={classes.tdMain}>{providerName}</td>
                          <td>
                            <span
                              className={cx(classes.pill, {
                                [classes.pillExternal]: isExternal,
                                [classes.pillOgiso]: !isExternal,
                              })}
                            >
                              {isExternal ? "External" : "Ogiso"}
                            </span>
                          </td>
                          <td>
                            {isExternal ? (
                              item?.ehrSystem ? (
                                <span className={classes.ehrChip}>{item.ehrSystem}</span>
                              ) : (
                                <span className={classes.mutedCell}>&mdash;</span>
                              )
                            ) : (
                              <span className={classes.mutedCell}>Ogiso</span>
                            )}
                          </td>
                          <td className={classes.num}>
                            {item?.documentCount ?? 0}
                          </td>
                          <td>
                            {item?.createdAt
                              ? moment(item.createdAt).format("MMM D, YYYY")
                              : "-"}
                          </td>
                          <td onClick={(e) => e.stopPropagation()}>
                            <Menu shadow="md" position="bottom-end" zIndex={1000}>
                              <Menu.Target>
                                <ActionIcon className={classes.kebab}>
                                  <BiDotsVerticalRounded size={18} />
                                </ActionIcon>
                              </Menu.Target>
                              <Menu.Dropdown
                                classNames={{
                                  dropdown: classes.rowMenuDropdown,
                                  item: classes.rowMenuItem,
                                }}
                              >
                                {!isPending && (
                                  <>
                                    {canEnterProviderView && (
                                      <Menu.Item
                                        icon={<IconArrowRight size={14} />}
                                        onClick={() => handleRowClick(item)}
                                      >
                                        Enter provider view
                                      </Menu.Item>
                                    )}
                                    <Menu.Item
                                      icon={<IconEye size={14} />}
                                      onClick={() =>
                                        navigate(`/home/provider-skypoint/view`, {
                                          state: { data: item },
                                        })
                                      }
                                    >
                                      View provider
                                    </Menu.Item>
                                    {/* Jumps straight to the provider's
                                        document store — the Documents tab of
                                        the same profile page. */}
                                    <Menu.Item
                                      icon={<IconFiles size={14} />}
                                      onClick={() =>
                                        navigate(`/home/provider-skypoint/view`, {
                                          state: { data: item, tab: "documents" },
                                        })
                                      }
                                    >
                                      Documents
                                    </Menu.Item>
                                    {profile?.user?.role !== "staff" && selectedTab !== "deleted" && (
                                      <Menu.Item
                                        icon={<IconEdit size={14} />}
                                        onClick={() =>
                                          navigate(`/home/provider-skypoint/edit`, {
                                            state: { data: item },
                                          })
                                        }
                                      >
                                        Edit
                                      </Menu.Item>
                                    )}
                                  </>
                                )}
                                {profile?.user?.role !== "staff" &&
                                  selectedTab !== "deleted" && (
                                    <>
                                      <Menu.Divider />
                                      {isExternal ? (
                                        <Menu.Item
                                          icon={<IconTrash size={14} />}
                                          color="red"
                                          onClick={() => handleDeleteProvider(item)}
                                        >
                                          Delete provider
                                        </Menu.Item>
                                      ) : (
                                        <Menu.Item
                                          icon={<IconUnlink size={14} />}
                                          color="red"
                                          onClick={() => handleUnlinkProvider(item)}
                                        >
                                          Remove from Skypoint
                                        </Menu.Item>
                                      )}
                                    </>
                                  )}
                              </Menu.Dropdown>
                            </Menu>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </Box>
            <Box className={classes.cardPagination}>
              <Text className={classes.pagInfo}>{pagInfo}</Text>
              <CustomBillingPagination
                data={skypointProvidersCount}
                itemsPerPage={10}
                currentPage={currentPage}
                handlePageChange={handlePageChange}
              />
            </Box>
          </Box>
        )}

        {!showTableCard && (
          <Box className={classes.emptyProviderBox}>
            <Flex align="center" justify="center" className={classes.iconBox}>
              <NoProviderSvg />
            </Flex>
            <Text fz={20} fw={600} ff="Inter" color="#171717">
              Provider
            </Text>
            <Text
              mt={10}
              mb={12}
              fz={16}
              fw={400}
              ff="Inter"
              color="#737373"
              maw={500}
              ta="center"
            >
              No Provider Found
            </Text>
            {canAddProviders(profile?.user, providerSettings?.whoCanAdd) && (
              <button type="button" className={classes.btnAdd} onClick={handleAddProvider}>
                <IconPlus size={15} stroke={2.4} />
                Add Provider
              </button>
            )}
          </Box>
        )}
      </Box>

      <AddProviderDrawer
        opened={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onAdded={() => {
          setDrawerOpen(false);
          refreshList();
        }}
      />
    </Box>
  );
};

export default SkypointProvider;
